import { Gauge, Infinity as InfinityIcon, CalendarClock } from "lucide-react";
import type { UsageInfo, UsageWindow } from "@/lib/agentcore";
import { relativeFuture } from "@/lib/time";

/**
 * UsagePanel - a clear, friendly "Your usage" card so the user always knows
 * where they stand on the demo's plan limits.
 *
 * Shows BOTH windows (today + this month) with a small progress bar, the count
 * ("X of 5 today"), and when each one refreshes (relativeFuture). A one-line
 * plain-English explainer reassures that saved briefs always stay available.
 *
 * Special cases:
 *   - unlimited === true  -> "Unlimited analyses" (no numbers, no bars, no gate).
 *   - status "suspended"  -> a calm paused-account note.
 *   - usage null / both limits 0 (feature unconfigured / local dev) -> renders
 *     NOTHING, so the awareness UI degrades silently.
 *
 * No backend / infra terms are surfaced - it speaks in plain product language.
 */
export function UsagePanel({ usage, className = "" }: { usage: UsageInfo | null; className?: string }) {
  // Degrade silently when usage is unknown / unconfigured.
  if (!usage) return null;

  const unlimited = usage.unlimited === true;
  const hasDaily = usage.daily.limit > 0;
  const hasMonthly = usage.monthly.limit > 0;

  // Nothing meaningful to show (older / unconfigured backend) -> render nothing,
  // unless the account is explicitly unlimited or suspended (worth telling).
  if (!unlimited && usage.status !== "suspended" && !hasDaily && !hasMonthly) {
    return null;
  }

  return (
    <section
      className={`ns-glass rounded-2xl p-5 shadow-card ${className}`}
      aria-label="Your usage"
    >
      <div className="flex items-center gap-2 mb-1">
        <Gauge className="w-3.5 h-3.5 text-[#22d3ee]" aria-hidden="true" />
        <span className="text-[10px] font-semibold text-[#22d3ee] uppercase tracking-[0.2em]">
          Your usage
        </span>
      </div>

      {unlimited ? (
        <div className="mt-2 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-700/40 bg-emerald-500/10 text-emerald-300 shrink-0">
            <InfinityIcon className="w-4 h-4" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-semibold text-white">Unlimited analyses</p>
            <p className="text-[11px] text-slate-500">Run as many briefs as you need - no daily or monthly cap.</p>
          </div>
        </div>
      ) : usage.status === "suspended" ? (
        <p className="mt-2 text-sm text-amber-300/90 leading-relaxed">
          Your account is paused right now, so new analyses are on hold. Your saved briefs stay available.
        </p>
      ) : (
        <>
          <div className="mt-3 flex flex-col gap-4">
            {hasDaily && <UsageBar label="Today" window={usage.daily} />}
            {hasMonthly && <UsageBar label="This month" window={usage.monthly} />}
          </div>
          <p className="mt-4 text-[11px] text-slate-500 leading-relaxed">
            Your plan includes a set number of analyses each day and month. Your saved briefs and
            watchlist always stay available.
          </p>
        </>
      )}
    </section>
  );
}

/** One window's row: "X of N <label>", a progress bar, and the reset time. */
function UsageBar({ label, window: w }: { label: string; window: UsageWindow }) {
  const pct = w.limit > 0 ? Math.min(100, Math.max(0, (w.used / w.limit) * 100)) : 0;
  const spent = w.remaining <= 0;
  const reset = relativeFuture(w.resetsAt);

  return (
    <div>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="text-xs font-medium text-slate-200">
          {w.used} of {w.limit} {label.toLowerCase()}
        </span>
        <span className="text-[11px] font-mono tabular-nums text-slate-500 shrink-0">
          {Math.max(0, w.remaining)} left
        </span>
      </div>
      <div
        className="h-2 rounded-full bg-[#0a1322] border border-[#1a2640]/60 overflow-hidden"
        role="meter"
        aria-valuenow={w.used}
        aria-valuemin={0}
        aria-valuemax={w.limit}
        aria-label={`${label}: ${w.used} of ${w.limit} analyses used`}
      >
        <div
          className={`h-full rounded-full bg-gradient-to-r ${
            spent ? "from-amber-500 to-amber-400" : "from-brand-500 to-[#22d3ee]"
          }`}
          style={{ width: `${pct}%`, transition: "width 700ms cubic-bezier(0.16,1,0.3,1)" }}
        />
      </div>
      {reset && (
        <p className="mt-1.5 flex items-center gap-1.5 text-[11px] text-slate-500">
          <CalendarClock className="w-3 h-3 text-slate-600" aria-hidden="true" />
          {spent ? `More open up ${reset}` : `Refreshes ${reset}`}
        </p>
      )}
    </div>
  );
}
